import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "bf_pwa_dismiss";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      // Re-prompt at most every 14 days
      if (Date.now() - dismissedAt > 14 * 24 * 60 * 60 * 1000) {
        setVisible(true);
      }
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible || !deferred) return null;

  const install = async () => {
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      // User dismissed the native prompt — non-fatal.
      console.warn("PWA install prompt cancelled", e);
    }
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 p-4 rounded-lg shadow-lg"
      style={{ background: "white", border: "1px solid var(--bf-border)" }}
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-md flex items-center justify-center font-serif text-xl flex-shrink-0"
          style={{ background: "var(--bf-primary)", color: "white" }}
        >
          B
        </div>
        <div className="flex-1">
          <p className="font-serif text-lg leading-tight mb-1">Install Bookforge</p>
          <p className="text-xs mb-3" style={{ color: "var(--bf-text-muted)" }}>
            Add it to your home screen for one-tap access and offline reading of your drafts.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={install}
              className="text-white"
              style={{ background: "var(--bf-primary)" }}
              data-testid="pwa-install-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Install
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} data-testid="pwa-dismiss-btn">
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-1 -mt-1 opacity-60 hover:opacity-100"
          aria-label="Dismiss"
          data-testid="pwa-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
